<template>
  <div class="supplement-label">
    <div class="preview-container" data-scroll-hint>
      <div
        ref="labelWrapperRef"
        class="label-wrapper"
        :class="{ 'is-calculating': store.isCalculating }"
        :style="wrapperStyle"
      >
        <div
          v-if="showLogo"
          id="label-logo-wrapper"
          v-tippy="getTooltip('label-settings')"
          :style="logoWrapperStyle"
        >
          <!-- eslint-disable vue/no-v-html -- SVG from user uploads; authenticated upload-only, user's own content -->
          <div
            v-if="store.logoImage.svg && store.logoImage.svg_content"
            v-html="store.logoImage.svg_content"
          />
          <!-- eslint-enable vue/no-v-html -->
          <img v-else :src="store.logoImage.image_url" alt="Logo" />
        </div>

        <SupplementFactsPanel v-if="!store.hideFacts" :is-touch="isTouch" />

        <template v-for="section in textSections" :key="section.fieldType">
          <LabelTextSection
            v-if="!section.hide"
            :title="section.title"
            :prefix="section.prefix"
            :placeholder="section.placeholder"
            :rows="section.rows"
            :test-id="section.testId"
            :field-type="section.fieldType"
            :default-value-key="section.defaultValueKey"
            :bold-label-key="section.boldLabelKey"
            :bold-text-key="section.boldTextKey"
            :auto-add-prefix="section.autoAddPrefix"
          />
        </template>

        <div v-if="store.bioengineered" class="supplement-bioengineered" :style="textAlignStyle">
          Contains Bioengineered Food Ingredient
        </div>

        <SupplementBarcode
          v-if="showBarcode"
          v-tippy="getTooltip('label-settings')"
          :barcode="store.primaryBarcode"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed, ref, watch, onMounted, onUnmounted, nextTick } from "vue";
  import { directive as vTippy } from "vue-tippy";
  import { useSupplementLabelStore } from "./stores/supplement-label-store";
  import SupplementFactsPanel from "./supplement-editor/supplement-facts-panel.vue";
  import LabelTextSection from "./supplement-editor/label-text-section.vue";
  import SupplementBarcode from "./supplement-editor/supplement-barcode.vue";
  import { useDimensions } from "./lib/use-dimensions";
  import "~/jose_modern/scroll_hint";

  const store = useSupplementLabelStore();
  const labelWrapperRef = ref(null);

  let mediaQuery = null;
  let mediaQueryHandler = null;

  const showLogo = computed(() => store.logoImage && !store.hideLogo);
  const showBarcode = computed(() => store.primaryBarcode && !store.hideBarcode);

  const wrapperStyle = computed(() => ({
    width: `${store.effectiveLabelWidth}px`,
    color: store.textColor,
    backgroundColor: store.transparentBackground ? "transparent" : store.backgroundColor,
  }));

  const logoWrapperStyle = computed(() => ({
    "--print-max-width": `${store.effectiveLabelWidth}px`,
    "--scale": store.logoScale,
    "--margin": `${store.logoMargin}px`,
    "zIndex": "auto", // Override logo.scss z-index: -1 which breaks in sticky stacking context
  }));

  const textAlignStyle = computed(() => ({
    textAlign: store.textAlign,
  }));

  const textSections = computed(() => [
    {
      fieldType: "otherIngredients",
      title: "Other Ingredients",
      prefix: "Other ingredients: ",
      placeholder: "e.g., Gelatin capsule, rice flour, magnesium stearate",
      hide: store.hideOtherIngredients,
      testId: "other-ingredients-input",
      boldLabelKey: "other_ingredients_label",
      boldTextKey: "all_other_ingredients",
    },
    {
      fieldType: "allergens",
      title: "Allergens",
      prefix: "Contains: ",
      placeholder: "Milk, Soy, Wheat",
      hide: store.hideAllergens,
      defaultValueKey: "defaultAllergens",
      testId: "allergens-input",
      boldLabelKey: "allergens_label",
      boldTextKey: "allergens",
    },
    {
      fieldType: "facilityAllergens",
      title: "Facility Allergens",
      prefix: store.defaultFacilityAllergensPrefix,
      placeholder: "Peanuts, Tree Nuts",
      hide: store.hideFacilityAllergens,
      defaultValueKey: "defaultFacilityAllergens",
      testId: "facility-allergens-input",
      boldLabelKey: "facility_allergens_label",
      boldTextKey: "facility_allergens",
      autoAddPrefix: false,
    },
    {
      fieldType: "businessInfo",
      title: "Business Info",
      prefix: "Manufactured for: ",
      placeholder: "My Company, Inc.\n123 Main St.\nAnytown, USA 12345",
      hide: store.hideBusinessInfo,
      rows: 3,
      testId: "business-info-input",
      boldTextKey: "business_info",
      autoAddPrefix: false,
    },
  ]);

  // Dimension measurement
  const { dimensionsText, refresh: refreshDimensions } = useDimensions(() => labelWrapperRef.value);

  // Expose dimensions to parent/store
  defineExpose({
    dimensionsText,
  });

  // Sync dimensions to store for Settings section display
  watch(dimensionsText, (newText) => {
    if (newText) {
      store.setRenderedDimensions(newText);
    }
  });

  // Refresh dimensions when visibility or scale changes affect layout
  watch(
    () => store.layoutSignature,
    () => {
      // Wait for Vue to update DOM, then refresh
      requestAnimationFrame(() => {
        refreshDimensions();
      });
    }
  );

  // Tooltip Logic
  const isTouch = ref(false);

  // Titles mapping (Desktop / Mobile)
  const SECTION_TITLES = {
    "serving": { desktop: "Serving Information", mobile: "Serving" },
    "dietary-info": { desktop: "Dietary Information", mobile: "Dietary" },
    "dv-lines": { desktop: "Ingredients with Daily Values", mobile: "Ingredients with DV" },
    "lines": { desktop: "Ingredients without DVs", mobile: "Ingredients without DV" },
    "blends": { desktop: "Proprietary Blends", mobile: "Blends" },
    "label-settings": { desktop: "Label Sections and Style", mobile: "Label Settings" },
  };

  function getTooltip(sectionKey) {
    if (!sectionKey || store.isPublic) return null;
    if (!SECTION_TITLES[sectionKey]) return null;

    const titles = SECTION_TITLES[sectionKey];
    const title = isTouch.value ? titles.mobile : titles.desktop;

    return {
      content: `Edit in <span class="tooltip-link" data-section="${sectionKey}" style="cursor: pointer; font-weight: bold;">${title}</span>`,
      // placement: auto is default behavior
      trigger: "mouseenter click",
      allowHTML: true,
      interactive: true,
      theme: "recipal",
      onShow(instance) {
        if (window.matchMedia("print").matches) return false;
      },
      onMount(instance) {
        const links = instance.popper.querySelectorAll(".tooltip-link");
        links.forEach((link) => {
          link.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            const key = e.target.closest(".tooltip-link").dataset.section;
            window.dispatchEvent(new CustomEvent("supplement:open-section", { detail: { key } }));
            instance.hide();
          });
        });
      },
    };
  }

  onMounted(() => {
    mediaQuery = window.matchMedia("(max-width: 1023px)");
    isTouch.value = mediaQuery.matches;
    mediaQueryHandler = (e) => {
      isTouch.value = e.matches;
    };
    try {
      mediaQuery.addEventListener("change", mediaQueryHandler);
    } catch (e) {
      mediaQuery.addListener?.(mediaQueryHandler);
    }
    nextTick(() => {
      document.body.dataset.supplementLabelReady = "true";
      window.supplementLabelSettled = () => {
        return document.body.dataset.supplementLabelReady === "true";
      };
    });
  });

  onUnmounted(() => {
    if (mediaQuery && mediaQueryHandler) {
      try {
        mediaQuery.removeEventListener("change", mediaQueryHandler);
      } catch (e) {
        mediaQuery.removeListener?.(mediaQueryHandler);
      }
    }
    delete window.supplementLabelSettled;
  });
</script>

<style lang="scss" scoped>
  @import "~jose_modern/css";

  .supplement-label {
    position: sticky;
    top: 8px;

    @include from($desktop) {
      top: 5rem; // Clear the sticky page header when stuck
    }
  }

  .preview-container {
    padding: 0 0 2rem;
    overflow-x: auto; /* allow scroll hint detection when content overflows */
    overflow-y: hidden;
    text-align: center;
    @media (max-width: 768px) {
      padding: 0 0 1rem;
    }
  }

  .label-wrapper {
    display: flex;
    flex-direction: column;
    width: auto;
    max-width: 100%;
    margin: 0 auto;
    /* No overflow restriction - allow content to overflow for scroll hint */
    text-align: left;

    // Override Bulma's `strong { color: $black }` so bold text in child components
    // (e.g. Tiptap editors in text sections) respects the user-chosen font color.
    :deep(strong),
    :deep(b) {
      color: inherit;
    }
  }

  @keyframes pulse-transparency {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.4;
    }
  }

  .supplement-bioengineered {
    font-family: Arial, Helvetica, sans-serif;
    font-size: 10px;
    line-height: 1.4;
    margin-top: 0.5rem;
  }

  .printlike-supplement-title {
    display: none;
    font-family: "Lato", sans-serif;
    color: rgb(10, 10, 10);
    font-size: 22px;
    line-height: 30px;
    margin-bottom: 0.75em;
    /* Align with label-wrapper: constrain width and center the block */
    max-width: 100%;
    margin-left: auto;
    margin-right: auto;
    text-align: left;

    &.is-hidden-title {
      display: none !important;
    }
  }

  @media print {
    .preview-header {
      display: none !important;
    }

    .preview-container {
      background: transparent;
      padding: 0;
      justify-content: flex-start;
    }

    .printlike-supplement-title:not(.is-hidden-title) {
      display: block;
    }
  }
</style>
